import React from 'react';
import PropTypes from 'prop-types';

const WIDTH = 232;
const HEIGHT = 58;
const PLOT_TOP = 12;
const PLOT_BOTTOM = 49;
const PLOT_LEFT = 5;
const PLOT_RIGHT = WIDTH - 5;

function isNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function hourLabel(hour) {
  if (hour === 12) return '12 PM';
  return hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
}

function compactHourLabel(hour) {
  if (hour === 12) return '12P';
  return hour > 12 ? `${hour - 12}P` : `${hour}A`;
}

function hourLabelAnchor(index, length) {
  if (index === 0) return 'start';
  if (index === length - 1) return 'end';
  return 'middle';
}

function barMarginClass(point, thresholds) {
  if (point.status === 'ahead') return 'on-pace';
  if (point.status === 'neutral') return 'warning';
  if (point.status === 'behind' || point.status === 'pending') return point.status;

  const shortfall = point.average - point.current;

  if (shortfall < thresholds.warningPoints) return 'on-pace';
  if (shortfall < thresholds.criticalPoints) return 'warning';
  return 'behind';
}

function PaceSparkline({
  label,
  referenceLabel,
  referenceStartHour,
  showAllHourLabels,
  thresholds,
  timeline,
  todayLabel,
  unit,
}) {
  if (!Array.isArray(timeline) || timeline.length === 0) return null;

  const values = timeline.flatMap((point) => [point.average, point.current]).filter(isNumber);
  const maxValue = unit === 'progress' ? 100 : Math.max(1, ...values);
  const step = (PLOT_RIGHT - PLOT_LEFT) / Math.max(1, timeline.length - 1);
  const xFor = (index) => PLOT_LEFT + (index * step);
  const yFor = (value) => PLOT_BOTTOM
    - ((Math.max(0, value) / maxValue) * (PLOT_BOTTOM - PLOT_TOP));
  const linePoints = timeline
    .map((point, index) => `${xFor(index)},${yFor(point.average)}`)
    .join(' ');
  const referenceLinePath = timeline.reduce((path, point, index) => {
    const x = xFor(index);
    const y = yFor(point.average);

    if (index === 0) return `M ${x} ${y}`;

    if (point.hour === referenceStartHour) {
      const previousY = yFor(timeline[index - 1].average);
      return `${path} L ${x} ${previousY} L ${x} ${y}`;
    }

    return `${path} L ${x} ${y}`;
  }, '');
  const latestPoint = [...timeline].reverse().find((point) => isNumber(point.current));
  const description = latestPoint
    ? `${label}: today's hourly readings through ${hourLabel(latestPoint.hour)} as bars; ${referenceLabel.toLowerCase()} as a line.`
    : `${label}: ${referenceLabel.toLowerCase()} as a line; today's readings have not started.`;

  return (
    <div className="today-pace-sparkline">
      <div className="today-pace-sparkline__legend" aria-hidden="true">
        <span className="today-pace-sparkline__legend-item today-pace-sparkline__legend-item--bar">
          {todayLabel}
        </span>
        <span className="today-pace-sparkline__legend-item today-pace-sparkline__legend-item--line">
          {referenceLabel}
        </span>
      </div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={description}>
        <line
          className="today-pace-sparkline__baseline"
          x1={PLOT_LEFT}
          x2={PLOT_RIGHT}
          y1={PLOT_BOTTOM}
          y2={PLOT_BOTTOM}
        />
        <g className="today-pace-sparkline__bars">
          {timeline.map((point, index) => {
            if (!isNumber(point.current)) return null;

            const y = yFor(point.current);
            return (
              <rect
                className={`today-pace-sparkline__bar--${barMarginClass(point, thresholds)}`}
                key={point.hour}
                x={xFor(index) - 4}
                y={y}
                width="8"
                height={Math.max(0, PLOT_BOTTOM - y)}
              >
                {isNumber(point.bar_target) && (
                  <title>
                    {`${hourLabel(point.hour)} mark: ${point.current}% against ${point.bar_target}% target`}
                  </title>
                )}
              </rect>
            );
          })}
        </g>
        {referenceStartHour !== null ? (
          <path className="today-pace-sparkline__average" d={referenceLinePath} />
        ) : (
          <polyline className="today-pace-sparkline__average" points={linePoints} />
        )}
        <g className="today-pace-sparkline__reference-labels">
          {timeline.map((point, index) => point.reference_label && (
            <text key={`reference-${point.hour}`} x={xFor(index)} y={PLOT_TOP - 4} textAnchor="middle">
              {point.reference_label}
            </text>
          ))}
        </g>
        {showAllHourLabels ? timeline.map((point, index) => (
          <text
            key={`label-${point.hour}`}
            x={xFor(index)}
            y={HEIGHT - 1}
            textAnchor={hourLabelAnchor(index, timeline.length)}
          >
            {compactHourLabel(point.hour)}
          </text>
        )) : (
          <>
            <text x={PLOT_LEFT} y={HEIGHT - 1}>8A</text>
            <text className="today-pace-sparkline__end-label" x={PLOT_RIGHT} y={HEIGHT - 1}>6P</text>
          </>
        )}
      </svg>
    </div>
  );
}

PaceSparkline.propTypes = {
  label: PropTypes.string.isRequired,
  referenceLabel: PropTypes.string,
  referenceStartHour: PropTypes.number,
  showAllHourLabels: PropTypes.bool,
  thresholds: PropTypes.shape({
    warningPoints: PropTypes.number.isRequired,
    criticalPoints: PropTypes.number.isRequired,
  }).isRequired,
  timeline: PropTypes.arrayOf(PropTypes.shape({
    hour: PropTypes.number.isRequired,
    current: PropTypes.number,
    average: PropTypes.number.isRequired,
    bar_target: PropTypes.number,
    reference_label: PropTypes.string,
    status: PropTypes.string,
  })),
  todayLabel: PropTypes.string,
  unit: PropTypes.string.isRequired,
};

PaceSparkline.defaultProps = {
  referenceLabel: '8-wk avg',
  referenceStartHour: null,
  showAllHourLabels: false,
  timeline: [],
  todayLabel: 'Today',
};

export default PaceSparkline;
